/**
 * フロー操作(承認・支払い・差戻し・取消など)の結果の伝え方(2026-09-17 改修依頼 B)。
 *
 * - 状態の変更(本体)が済んだあとの通知・既読化・操作履歴は「付随処理」。失敗しても本体は取り消さず、
 *   成功として扱ったうえで、何に失敗したかを警告で知らせる
 *   (以前は通知だけ失敗しても「差戻しに失敗しました」と出て、やり直すと権限エラーになっていた)
 * - ほかの人が先に操作して状態が変わっていた場合は、英語の権限エラーではなく今の状態を日本語で伝える
 */
import { doc, getDoc } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { STATUS_LABELS, type Transaction, type TransactionStatus } from '@core/lib/types';

export type FlowResult = {
  /** 付随処理の一部に失敗したときの説明。null なら全部成功 */
  warning: string | null;
};

/** 画面を開いたあとに、ほかの人の操作で申請の状態が変わっていた */
export class FlowStaleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FlowStaleError';
  }
}

export function isFlowStaleError(e: unknown): e is FlowStaleError {
  return e instanceof FlowStaleError;
}

/** 状態は変わっていないのに権限ルールで拒否された(権限がない・内容が条件を満たさない) */
export class FlowPermissionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FlowPermissionError';
  }
}

/**
 * 本体の書き込み。権限エラーのときは最新の状態を読み直し、状態が変わっていれば FlowStaleError にする
 */
export async function commitMain(
  targets: Array<Pick<Transaction, 'id' | 'code' | 'status'>>,
  write: () => Promise<unknown>,
): Promise<void> {
  try {
    await write();
  } catch (e) {
    if ((e as { code?: string } | null)?.code !== 'permission-denied') throw e;
    for (const t of targets) {
      const latest = await getDoc(doc(getDb(), 'transactions', t.id))
        .then((s) => (s.exists() ? ((s.data().status as TransactionStatus | undefined) ?? null) : 'deleted'))
        .catch(() => null);
      if (latest === 'deleted') {
        throw new FlowStaleError(`${t.code ?? 'この申請'} は削除されています。一覧を更新して確認してください`);
      }
      if (latest && latest !== t.status) {
        throw new FlowStaleError(
          `${t.code ?? 'この申請'} は、ほかの人の操作ですでに「${STATUS_LABELS[latest]}」になっています。一覧を更新して確認してください`,
        );
      }
    }
    throw new FlowPermissionError(
      'この操作を行う権限がないか、内容が操作の条件を満たしていません。画面を更新してから、もう一度お試しください',
    );
  }
}

/**
 * 付随処理を順に実行する。1つ失敗しても残りは続ける
 */
export async function runFollowUps(steps: Array<[label: string, run: () => Promise<unknown>]>): Promise<FlowResult> {
  const failed: string[] = [];
  for (const [label, run] of steps) {
    try {
      await run();
    } catch (e) {
      console.error(`付随処理に失敗しました: ${label}`, e);
      if (!failed.includes(label)) failed.push(label);
    }
  }
  return {
    warning:
      failed.length === 0
        ? null
        : `ただし、${failed.join('・')}に失敗しました。操作そのものは完了しています。必要に応じて関係者へ直接お知らせください。`,
  };
}

/** 成功を知らせ、付随処理の失敗があれば閉じるまで残る警告でも知らせる */
export function reportFlowResult(toast: (message: string) => void, message: string, result: FlowResult): void {
  toast(message);
  if (result.warning) window.alert(`${message}\n\n${result.warning}`);
}
